import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Separator } from '../../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { customerDemoBookingService } from '../../services/customerDemoBookingService';
import { useToast } from '../../hooks/use-toast';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Loader2,
  ArrowLeft,
  Phone,
  Mail,
  BookOpen,
  CheckCircle,
  XCircle,
  AlertCircle,
  CalendarCheck,
} from 'lucide-react';

const DetailSkeleton = () => (
  <div className="space-y-6">
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <Skeleton className="h-20 w-20 rounded-full" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </CardContent>
    </Card>
  </div>
);

const getStatusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'confirmed':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'completed':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'cancelled':
      return 'bg-red-100 text-red-800 border-red-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'pending':
      return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    case 'confirmed':
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    case 'completed':
      return <CalendarCheck className="h-5 w-5 text-blue-600" />;
    case 'cancelled':
      return <XCircle className="h-5 w-5 text-red-600" />;
    default:
      return null;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 || 12;
  return `${formattedHour}:${minutes} ${ampm}`;
};

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchBooking();
  }, [id]);

  const fetchBooking = async () => {
    setLoading(true);
    try {
      const response = await customerDemoBookingService.getBooking(id);
      if (response.success) {
        setBooking(response.data);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch booking details',
        variant: 'destructive',
      });
      navigate('/customer/bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      const response = await customerDemoBookingService.cancelBooking(id);
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Booking cancelled successfully',
        });
        setCancelDialogOpen(false);
        fetchBooking();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to cancel booking',
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleContactTutor = () => {
    if (booking?.tutor?.phone) {
      window.location.href = `tel:${booking.tutor.phone}`;
    } else if (booking?.tutor?.email) {
      window.location.href = `mailto:${booking.tutor.email}`;
    }
  };

  const renderStatusTimeline = () => {
    const statuses = ['pending', 'confirmed', 'completed'];
    const currentIndex = statuses.indexOf(booking?.status);
    const isCancelled = booking?.status === 'cancelled';

    if (isCancelled) {
      return (
        <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
          <XCircle className="h-6 w-6 text-red-600" />
          <div>
            <p className="font-semibold text-red-800">Booking Cancelled</p>
            <p className="text-sm text-red-600">
              {booking.cancelledAt
                ? `Cancelled on ${formatDate(booking.cancelledAt)}`
                : 'This booking has been cancelled'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        {statuses.map((status, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <React.Fragment key={status}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isCompleted
                      ? isCurrent
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {status === 'pending' && <AlertCircle className="h-5 w-5" />}
                  {status === 'confirmed' && <CheckCircle className="h-5 w-5" />}
                  {status === 'completed' && <CalendarCheck className="h-5 w-5" />}
                </div>
                <p
                  className={`text-xs mt-2 capitalize ${
                    isCompleted ? 'text-gray-800 font-medium' : 'text-gray-400'
                  }`}
                >
                  {status}
                </p>
              </div>
              {index < statuses.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    index < currentIndex ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <DetailSkeleton />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="text-center py-12">
          <CardContent>
            <AlertCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Booking not found</h3>
            <Button onClick={() => navigate('/customer/bookings')} className="mt-4">
              Go to Bookings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Button variant="ghost" className="mb-6 gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />
        Back to Bookings
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={booking.tutor?.photo} alt={booking.tutor?.name} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-600 to-teal-500 text-white text-2xl">
                      {booking.tutor?.name?.charAt(0) || 'T'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-2xl">{booking.tutor?.name || 'Tutor'}</CardTitle>
                    <p className="text-gray-500 mt-1">Demo Session</p>
                  </div>
                </div>
                <Badge className={`${getStatusColor(booking.status)} text-base px-4 py-1`}>
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <Calendar className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="font-medium">{formatDate(booking.demoDate || booking.demo_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="font-medium">{formatTime(booking.demoTime || booking.demo_time)}</p>
                  </div>
                </div>
              </div>

              {(booking.location || booking.address) && (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{booking.location || booking.address}</p>
                  </div>
                </div>
              )}

              <Separator />

              <div>
                <h4 className="font-semibold mb-4">Booking Status</h4>
                {renderStatusTimeline()}
              </div>
            </CardContent>
          </Card>

          {booking.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes / Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{booking.notes}</p>
              </CardContent>
            </Card>
          )}

          {booking.tutorResponse && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Tutor's Response</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 bg-blue-50 p-4 rounded-lg">{booking.tutorResponse}</p>
                {booking.respondedAt && (
                  <p className="text-xs text-gray-500 mt-2">
                    Responded on {formatDate(booking.respondedAt)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Student Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-500">Student Name</p>
                  <p className="font-medium">{booking.student?.name || 'N/A'}</p>
                </div>
              </div>
              {booking.student?.class && (
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-500">Class</p>
                    <p className="font-medium">{booking.student.class}</p>
                  </div>
                </div>
              )}
              {booking.student?.board && (
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-500">Board</p>
                    <p className="font-medium">{booking.student.board}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tutor Contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {booking.tutor?.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{booking.tutor.phone}</p>
                  </div>
                </div>
              )}
              {booking.tutor?.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{booking.tutor.email}</p>
                  </div>
                </div>
              )}
              {booking.tutor?.subjects && booking.tutor.subjects.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Subjects</p>
                  <div className="flex flex-wrap gap-2">
                    {booking.tutor.subjects.map((subject, index) => (
                      <Badge key={index} variant="secondary">
                        {subject}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {['pending', 'confirmed'].includes(booking.status) && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={handleCancelClick}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Booking
                </Button>
              )}
              {(booking.tutor?.phone || booking.tutor?.email) && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleContactTutor}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Contact Tutor
                </Button>
              )}
              {booking.status === 'completed' && (
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm text-green-800 font-medium">
                    Demo session completed
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this demo session with {booking.tutor?.name || 'the tutor'}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelling}>No, Keep It</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Yes, Cancel Booking'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingDetail;
